import { useInfiniteQuery } from 'react-query'

import { DrugRegistries, DrugRegistry } from '../../../types/drugRegistry'
import { Filtering, FilteringValue } from '../../../types/filtering'
import { Sorting } from '../../../types/sorting'
import { get } from '../../../utils/api'
import { getOffsetFromUrl, getQueryString } from '../../../utils/queryString'
import { serializeSorting } from '../../../utils/sorting'

// The three dimension columns filter with django-filter `__gte`/`__lte`
// lookups (e.g. `height__gte`), and the Minsan/description text filters use
// `__icontains` (e.g. `minsan__icontains`). Those keys live in the URL as
// plain query-string state managed by `useQueryParamFiltering`, which reads
// them back through `camelcaseKeys` — a double underscore is just another
// word boundary to it, so `height__gte` comes back out as `heightGte`. If
// that camelCase key were spread straight into `getQueryString`, it would get
// the default camelCase -> snake_case conversion (single underscore:
// `height_gte`), which is not a param name django-filter recognises — the
// filter would be silently dropped server-side instead of erroring. So we
// keep these keys in camelCase everywhere in the UI/state layer, and
// translate them back to their real double-underscore lookup name only here,
// right before the request is built.
export type DimensionFilterKey =
  | 'heightGte'
  | 'heightLte'
  | 'widthGte'
  | 'widthLte'
  | 'depthGte'
  | 'depthLte'

// Same double-underscore lookup as the dimension columns, for the two date
// range filters (last machined / first seen).
export type DateFilterKey =
  | 'lastMachinedAtGte'
  | 'lastMachinedAtLte'
  | 'firstSeenAtGte'
  | 'firstSeenAtLte'

export type TextLookupFilterKey = 'minsanIcontains' | 'descriptionIcontains'

// Custom single-underscore filters on the backend (`height_nan`,
// `geometry_anomaly`, ...): the default camelCase -> snake_case conversion
// already yields their real param name, so they need no LOOKUP_PARAM entry.
export type NanFilterKey = 'heightNan' | 'widthNan' | 'depthNan'

export type FilterKey =
  | keyof DrugRegistry
  | DimensionFilterKey
  | DateFilterKey
  | TextLookupFilterKey
  | NanFilterKey
  | 'geometryAnomaly'

const LOOKUP_PARAM: Record<
  DimensionFilterKey | DateFilterKey | TextLookupFilterKey,
  string
> = {
  heightGte: 'height__gte',
  heightLte: 'height__lte',
  widthGte: 'width__gte',
  widthLte: 'width__lte',
  depthGte: 'depth__gte',
  depthLte: 'depth__lte',
  lastMachinedAtGte: 'last_machined_at__gte',
  lastMachinedAtLte: 'last_machined_at__lte',
  firstSeenAtGte: 'first_seen_at__gte',
  firstSeenAtLte: 'first_seen_at__lte',
  minsanIcontains: 'minsan__icontains',
  descriptionIcontains: 'description__icontains',
}

const isLookupFilterKey = (
  key: string
): key is DimensionFilterKey | DateFilterKey | TextLookupFilterKey =>
  Object.prototype.hasOwnProperty.call(LOOKUP_PARAM, key)

/**
 * Rewrites the camelCase lookup keys (`heightGte`, `minsanIcontains`, ...) to
 * their real django-filter param name (`height__gte`, `minsan__icontains`,
 * ...) so that `getQueryString` — which only skips its own camelCase ->
 * snake_case conversion for keys that already contain an underscore — passes
 * them through unchanged instead of re-serializing them into a param the
 * backend doesn't recognise.
 */
export const toApiFiltering = (
  filtering: Filtering<Record<FilterKey, string>>
): Record<string, FilteringValue> =>
  Object.entries(filtering).reduce<Record<string, FilteringValue>>(
    (acc, [key, value]) => ({
      ...acc,
      [isLookupFilterKey(key) ? LOOKUP_PARAM[key] : key]: value,
    }),
    {}
  )

export const useDrugRegistry = ({
  sorting,
  filtering,
}: {
  sorting: Sorting
  filtering: Filtering<Record<FilterKey, string>>
}) => {
  const limit = 20

  const {
    data: response,
    isLoading,
    // `keepPreviousData: true` means `response` (and therefore `count`) keeps
    // showing the PREVIOUS filter's result while a new `filtering` value is
    // still in flight — `isLoading` stays `false` for those refetches, so it
    // cannot be used to detect them. `isFetching` is the only flag that goes
    // back to `true` for a keep-previous-data refetch, and consumers that
    // act on `count` (the bulk-action-by-filter confirmation) need it to
    // avoid operating on a stale count.
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ['drug-registry', sorting, filtering],
    ({ pageParam }) =>
      get<DrugRegistries>(
        `/drug-registry/?${getQueryString({
          offset: pageParam,
          limit,
          ...toApiFiltering(filtering),
          ordering: serializeSorting(sorting),
        })}`
      ),
    {
      getNextPageParam: (lastPage) => getOffsetFromUrl(lastPage.next),
      retry: false,
      keepPreviousData: true,
    }
  )

  const data = response?.pages.map((p) => p.results).flat() || []
  const isEmpty = !isLoading && data.length === 0
  const count = response?.pages[0]?.count ?? 0

  return {
    data,
    isLoading,
    isFetching,
    isEmpty,
    hasNextPage,
    fetchNextPage,
    count,
  }
}
